import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

export const DOCUMENT_REVIEW_PREFERENCE_KEY = 'review_document_data_before_generation';

export const DocumentGenerationPreferencesContext = createContext({
  reviewBeforeGeneration: false,
  loading: false,
  saving: false,
  setReviewBeforeGeneration: async () => true,
});

const readPreference = (payload, fallback = false) => {
  const value = payload?.preferences?.[DOCUMENT_REVIEW_PREFERENCE_KEY];
  return typeof value === 'boolean' ? value : fallback;
};

export const DocumentGenerationPreferencesProvider = ({ loggedIn, children }) => {
  const [reviewBeforeGeneration, setReviewBeforeGenerationState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const valueRef = useRef(false);
  const saveInFlightRef = useRef(false);
  const loadControllerRef = useRef(null);
  const saveControllerRef = useRef(null);
  const accountSessionRef = useRef(0);

  useEffect(() => {
    valueRef.current = reviewBeforeGeneration;
  }, [reviewBeforeGeneration]);

  useEffect(() => {
    const accountSession = accountSessionRef.current + 1;
    accountSessionRef.current = accountSession;
    loadControllerRef.current?.abort();
    saveControllerRef.current?.abort();
    loadControllerRef.current = null;
    saveControllerRef.current = null;
    saveInFlightRef.current = false;

    if (loggedIn !== true) {
      valueRef.current = false;
      setReviewBeforeGenerationState(false);
      setLoading(false);
      setSaving(false);
      saveInFlightRef.current = false;
      return undefined;
    }

    const controller = new AbortController();
    loadControllerRef.current = controller;
    setLoading(true);
    fetch('https://api.casedb.co/account.php', {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.message || 'Unable to load document generation settings.');
        }
        if (controller.signal.aborted || accountSessionRef.current !== accountSession) return;
        const enabled = readPreference(payload, false);
        valueRef.current = enabled;
        setReviewBeforeGenerationState(enabled);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError' && accountSessionRef.current === accountSession) {
          // Document review is opt-in, including when account settings are unavailable.
          valueRef.current = false;
          setReviewBeforeGenerationState(false);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted && accountSessionRef.current === accountSession) {
          loadControllerRef.current = null;
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
      saveControllerRef.current?.abort();
    };
  }, [loggedIn]);

  const setReviewBeforeGeneration = useCallback(async (enabled) => {
    const nextEnabled = Boolean(enabled);
    if (saveInFlightRef.current) return valueRef.current;

    const previousEnabled = valueRef.current;
    const accountSession = accountSessionRef.current;
    loadControllerRef.current?.abort();
    loadControllerRef.current = null;
    setLoading(false);
    const controller = new AbortController();
    saveControllerRef.current = controller;
    saveInFlightRef.current = true;
    valueRef.current = nextEnabled;
    setReviewBeforeGenerationState(nextEnabled);
    setSaving(true);

    try {
      await ensureCaseDbCsrfToken().catch(() => null);
      const response = await fetch('https://api.casedb.co/account.php', {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
        body: JSON.stringify({
          action: 'preferences',
          preferences: {
            [DOCUMENT_REVIEW_PREFERENCE_KEY]: nextEnabled,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || 'Unable to save document generation settings.');
      }
      if (accountSessionRef.current !== accountSession) return valueRef.current;

      const savedEnabled = readPreference(payload, nextEnabled);
      valueRef.current = savedEnabled;
      setReviewBeforeGenerationState(savedEnabled);
      return savedEnabled;
    } catch (error) {
      if (accountSessionRef.current !== accountSession || error?.name === 'AbortError') {
        return valueRef.current;
      }
      valueRef.current = previousEnabled;
      setReviewBeforeGenerationState(previousEnabled);
      throw error;
    } finally {
      if (accountSessionRef.current === accountSession) {
        saveControllerRef.current = null;
        saveInFlightRef.current = false;
        setSaving(false);
      }
    }
  }, []);

  const value = useMemo(() => ({
    reviewBeforeGeneration,
    loading,
    saving,
    setReviewBeforeGeneration,
  }), [loading, reviewBeforeGeneration, saving, setReviewBeforeGeneration]);

  return (
    <DocumentGenerationPreferencesContext.Provider value={value}>
      {children}
    </DocumentGenerationPreferencesContext.Provider>
  );
};

export const useDocumentGenerationPreferences = () => useContext(DocumentGenerationPreferencesContext);
